import React from 'react';
import { useFetcher } from 'react-router-dom';
import { ActionResult } from '@oysterjs/core/graphql/actions';
import { Button } from '@oysterjs/ui/button';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle
} from '@oysterjs/ui/dialog';
import { Description, Field, FieldGroup, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { UploadKnowledgeBaseFileDocumentMutation } from '~/api/graphql';
import { FileInput } from '~/components/file';
import { LibraryFileUploadProgress } from '~/components/library/library-file-upload-progress';
import { CopilotSubmitError } from '~/components/overage-limit-error';
import {
  type CopilotActionError,
  copilotActionErrorFromApplicationError
} from '~/lib/copilot-action-error';

export function KnowledgeBaseUploadDialog({
  open,
  onClose,
  knowledgeBaseId,
  folderId
}: {
  open: boolean;
  onClose: () => void;
  knowledgeBaseId: string;
  folderId?: string | null;
}) {
  const fetcher = useFetcher<ActionResult<UploadKnowledgeBaseFileDocumentMutation>>();
  const [files, setFiles] = React.useState<File[]>([]);
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<CopilotActionError>();
  const [pickerKey, setPickerKey] = React.useState(0);
  const pendingRef = React.useRef(false);
  const wasBusyRef = React.useRef(false);

  const reset = React.useCallback(() => {
    setFiles([]);
    setError(undefined);
    setPickerKey((key) => key + 1);
  }, []);

  React.useEffect(() => {
    if (!open && !pendingRef.current) reset();
  }, [open, reset]);

  React.useEffect(() => {
    if (!pendingRef.current) return;
    if (fetcher.state !== 'idle') {
      wasBusyRef.current = true;
      return;
    }
    if (!wasBusyRef.current) return;

    pendingRef.current = false;
    wasBusyRef.current = false;
    setPending(false);
    const result = fetcher.data;
    if (result?.applicationError) {
      setError(copilotActionErrorFromApplicationError(result.applicationError));
    } else if (result?.validationErrors?.length) {
      setError({
        kind: 'generic',
        message: result.validationErrors.map((e) => e.message).join(' ')
      });
    } else if (result?.data?.uploadKnowledgeBaseFileDocument?.length) {
      reset();
      onClose();
    } else {
      setError({ kind: 'generic', message: 'No documents were uploaded. Please try again.' });
    }
  }, [fetcher.state, fetcher.data, onClose, reset]);

  const handleClose = () => {
    if (pendingRef.current) return;
    reset();
    onClose();
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    // Lock synchronously: another submit can arrive before the fetcher renders its busy state.
    if (pendingRef.current || !files.length) return;
    pendingRef.current = true;
    wasBusyRef.current = false;
    setPending(true);
    setError(undefined);

    const formData = new FormData();
    formData.set('knowledgeBaseId', knowledgeBaseId);
    if (folderId) formData.set('folderId', folderId);
    files.forEach((file) => formData.append('files', file));
    fetcher.submit(formData, {
      method: 'POST',
      action: `/app/knowledge/${knowledgeBaseId}`,
      encType: 'multipart/form-data'
    });
  };

  const uploaded = Boolean(fetcher.data?.data?.uploadKnowledgeBaseFileDocument?.length);

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Upload documents</DialogTitle>
      <DialogDescription>
        Upload documents to the knowledge base. They will appear after upload while Cara indexes
        them in the background.
      </DialogDescription>
      <form onSubmit={handleSubmit}>
        <DialogBody>
          <div hidden={pending}>
            <Fieldset className="min-w-0">
              <FieldGroup>
                <Field>
                  <Label>Files</Label>
                  <Description>Currently only PDF files and images are supported.</Description>
                  <FileInput
                    key={pickerKey}
                    multiple
                    onChange={(change) => {
                      setError(undefined);
                      setFiles(
                        change.files.filter((file) => file.upload).map((file) => file.upload!)
                      );
                    }}
                  />
                </Field>
              </FieldGroup>
            </Fieldset>
          </div>
          {error && <CopilotSubmitError className="mt-4" error={error} />}
          {pending && (
            <div role="status">
              <LibraryFileUploadProgress
                files={files}
                complete={fetcher.state === 'loading' && uploaded}
              />
              <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
                {fetcher.state === 'loading' && uploaded
                  ? 'Upload complete. Updating your document list…'
                  : 'Uploading documents. Please keep this dialog open; large files may take a few minutes.'}
              </p>
            </div>
          )}
        </DialogBody>
        <DialogActions>
          <Button type="button" color="white" disabled={pending} onClick={handleClose}>
            Close
          </Button>
          <Button type="submit" color="sky" disabled={pending || !files.length} loading={pending}>
            Upload Documents
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
